/**
 * Widget showing live clock + countdown to the next episode release.
 * Reads from upcoming-episode store. Renders nothing if nothing scheduled.
 *
 * i18n: respects the active language (ar / fr / en) from ThemeContext.
 * After release: shows an animated "fire" decorative bar under the
 * Watch button (visual flair only).
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Clock, CalendarClock, Sparkles, PlayCircle, Lock } from "lucide-react";
import { useCountdown, breakdown } from "@/lib/upcoming-episode";
import { useTheme, type LanguageCode } from "@/lib/theme-context";
import { RealisticFire } from "./RealisticFire";

function pad(n: number) { return n.toString().padStart(2, "0"); }

const T = {
  ar: {
    units: ["يوم", "ساعة", "دقيقة", "ثانية"],
    watch: (n: number) => `مشاهدة الحلقة ${n}`,
    soon: (n: number) => `الحلقة ${n} — قريباً`,
    tooltip: "سيتم تفعيل الزر عند انتهاء العدّ التنازلي",
    msg: (n: number, s: string) => `الحلقة ${n} من «${s}»`,
    released: (n: number, s: string) => `تم إطلاق الحلقة ${n} من «${s}» — متاحة الآن 🎉`,
    dir: "rtl" as const,
    locale: "ar-MA",
  },
  fr: {
    units: ["j", "h", "min", "s"],
    watch: (n: number) => `Regarder l'épisode ${n}`,
    soon: (n: number) => `Épisode ${n} — bientôt`,
    tooltip: "Le bouton s'activera à la fin du compte à rebours",
    msg: (n: number, s: string) => `Épisode ${n} de « ${s} »`,
    released: (n: number, s: string) => `L'épisode ${n} de « ${s} » est disponible 🎉`,
    dir: "ltr" as const,
    locale: "fr-FR",
  },
  en: {
    units: ["d", "h", "m", "s"],
    watch: (n: number) => `Watch episode ${n}`,
    soon: (n: number) => `Episode ${n} — soon`,
    tooltip: "Button will unlock when the countdown ends",
    msg: (n: number, s: string) => `Episode ${n} of "${s}"`,
    released: (n: number, s: string) => `Episode ${n} of "${s}" is out now 🎉`,
    dir: "ltr" as const,
    locale: "en-US",
  },
} satisfies Record<LanguageCode, unknown>;

export function UpcomingEpisodeWidget({ compact = false }: { compact?: boolean }) {
  const { upcoming, diffMs, expired, now } = useCountdown();
  const { language } = useTheme();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  void tick;

  if (!upcoming) return null;

  const t = T[language];
  const b = breakdown(diffMs);
  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  const date = now.toLocaleDateString(t.locale, { weekday: "short", day: "numeric", month: "short" });

  const message =
    (language === "ar" ? upcoming.message?.trim() : "") || t.msg(upcoming.episodeNumber, upcoming.seriesTitle);
  const releasedMessage =
    (language === "ar" ? upcoming.releasedMessage?.trim() : "") ||
    t.released(upcoming.episodeNumber, upcoming.seriesTitle);

  const epSlug = `s${upcoming.seasonNumber}e${upcoming.episodeNumber}`;
  const explicitUrl = upcoming.episodeUrl?.trim();

  const watchBtnClass =
    "mt-2 inline-flex items-center justify-center gap-1.5 text-[11px] font-extrabold py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90";

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-2.5 flex flex-col gap-2 h-full ${compact ? "mb-0" : "mb-3"}`}
      dir={t.dir}
    >
      {/* Date + live clock — two equal cells */}
      <div className="grid grid-cols-2 gap-1.5">
        <div className="rounded-md bg-background/60 border border-border/60 px-1.5 py-1 flex items-center gap-1 min-w-0">
          <CalendarClock className="h-3 w-3 text-primary shrink-0" />
          <span className="text-[10px] font-semibold text-foreground/90 truncate">{date}</span>
        </div>
        <div className="rounded-md bg-background/60 border border-border/60 px-1.5 py-1 flex items-center justify-center gap-1">
          <Clock className="h-3 w-3 text-primary shrink-0" />
          <span className="font-mono text-[11px] font-extrabold tabular-nums text-primary">{time}</span>
        </div>
      </div>

      {/* Message */}
      <div className="flex items-start gap-1.5 rounded-md bg-primary/5 border border-primary/20 px-2 py-1.5">
        <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
        <p className="text-[11px] leading-snug font-semibold text-foreground/95 break-words">
          {expired ? releasedMessage : message}
        </p>
      </div>

      {/* Countdown — 4 equal cells */}
      {!expired ? (
        <div className="grid grid-cols-4 gap-1 text-center mt-auto">
          {[
            { v: b.days, l: t.units[0] },
            { v: b.hours, l: t.units[1] },
            { v: b.minutes, l: t.units[2] },
            { v: b.seconds, l: t.units[3] },
          ].map((s, i) => (
            <div key={i} className="rounded-md bg-background/70 border border-border/60 py-1.5 px-0.5 flex flex-col items-center justify-center">
              <div className="font-mono text-sm font-extrabold tabular-nums text-primary leading-none">
                {pad(s.v)}
              </div>
              <div className="text-[9px] text-muted-foreground mt-1 leading-none">{s.l}</div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Watch button — disabled while waiting, enabled on release */}
      {expired ? (
        <>
          {explicitUrl ? (
            <a href={explicitUrl} target="_blank" rel="noopener noreferrer" className={watchBtnClass}>
              <PlayCircle className="h-4 w-4" /> {t.watch(upcoming.episodeNumber)}
            </a>
          ) : (
            <Link
              to="/watch/$slug/$ep"
              params={{ slug: upcoming.seriesSlug, ep: epSlug }}
              onClick={() => { import("@/lib/internal-nav").then(m => m.allowNextInternalNav()); }}
              className={watchBtnClass}
            >
              <PlayCircle className="h-4 w-4" /> {t.watch(upcoming.episodeNumber)}
            </Link>
          )}
          {/* Decorative realistic fire — appears only after release.
              In compact (mobile menu) mode we shrink it so the widget height
              stays aligned with the adjacent nav-buttons column. */}
          <RealisticFire height={compact ? 28 : 64} />



        </>
      ) : (
        <button
          type="button"
          disabled
          aria-disabled="true"
          title={t.tooltip}
          className="mt-2 inline-flex items-center justify-center gap-1.5 text-[11px] font-extrabold py-2 rounded-md bg-muted text-muted-foreground border border-border/60 opacity-70 cursor-not-allowed"
        >
          <Lock className="h-3.5 w-3.5" /> {t.soon(upcoming.episodeNumber)}
        </button>
      )}
    </div>
  );
}

